import moment from 'moment'
import numeral from 'numeral'

export interface CellValueHolder {
    value: CellValue
}

export type CellValue = string | number | boolean

export interface HoverValueHolder {
    value: CellValue | undefined
}

export type CellValueFn<RowDataType> = (rowData: RowData<RowDataType>) => CellValue
export type HoverValueFn<RowDataType> = (rowData: RowData<RowDataType>) => CellValue | undefined

export type RowData<RowDataType> = RowDataType

export interface NestedTable<RowDataType> {
    isExpanded: boolean
    table: Table<RowDataType, undefined>
}

export interface RowOptions {
    isSelectable: boolean
    /** This will greatly affect performance for large tables */
    getDefaultCellColor?: () => string | undefined
    defaultCellColor?: string
}

export interface HoverOptions<RowDataType> {
    valueFn: (CellValueFn<RowDataType> | undefined)[]
    data: RowData<RowDataType>
}

export interface RowClickEvent<RowDataType, NestedRowDataType> {
    row: TableRow<RowDataType, NestedRowDataType>
    cellIndex: number
}

export interface TableOptions {
    isSortable: boolean
    pagination: {
        show: boolean
        defaultPageSize?: number
        showUnselectButton?: boolean
        showReloadButton?: boolean
        showDownloadButton?: boolean
        showExpandAllButton?: boolean
    }
    rowOptions?: RowOptions
    currentSort?: CurrentSort
    isServerSide?: boolean
    selectionOptions: {
        type: 'SINGLE' | 'MULTI' | 'NONE'
    }
    noResultsMessage?: string
    tableClass?: string
}

export interface CurrentSort {
    columnIndex: number
    ascending: boolean
    isServerSide?: boolean
}

export interface CurrentPage {
    limit: number
    skip: number
}

export interface TableParseParameters {
    limit: number
    skip: number
    ascending: boolean
    isServerSide: boolean
}

export type TableFetchQuery<RowDataType> = () => RowDataType[]

export class Table<RowDataType, NestedRowDataType> {
    private _rows: TableRow<RowDataType, NestedRowDataType>[] = []
    public pagination?: TablePagination
    isFetched = false
    tableClass: string

    constructor(
        public columns: TableColumn<RowDataType>[],
        rows: TableRow<RowDataType, NestedRowDataType>[],
        public fetchQueryFunction: TableFetchQuery<RowDataType> | undefined,
        public options: TableOptions = {
            isSortable: true,
            currentSort: { columnIndex: -1, ascending: true },
            pagination: {
                show: false,
            },
            isServerSide: false,
            selectionOptions: {
                type: 'NONE',
            },
        }
    ) {
        if (rows.length >= 0) {
            this.replaceRows(rows)
            this.isFetched = true
        }

        if (this.options.pagination.show) {
            const defaultPageSize = this.options.pagination.defaultPageSize ?? 25

            const showReloadButton = this.options.pagination.showReloadButton ?? true
            const showUnselectButton = (this.options.pagination.showUnselectButton ?? this.options.selectionOptions.type !== 'NONE') ? true : false
            const showDownloadButton = this.options.pagination.showDownloadButton ?? false
            const showExpandAllButton = this.options.pagination.showExpandAllButton ?? false

            this.pagination = new TablePagination(
                0,
                defaultPageSize,
                this.totalRows,
                this.options.isServerSide,
                showUnselectButton,
                showReloadButton,
                showDownloadButton,
                showExpandAllButton
            )
        }

        this.tableClass = options.tableClass ?? ''
    }

    get skip(): number {
        return this.pagination ? this.pagination.skip : 0
    }

    get limit(): number {
        return this.pagination ? this.pagination.limit : this.rows.length
    }

    get totalRows() {
        return this.pagination?.rowCount ?? this.rows.length
    }

    /**
     * Replace existing table rows to update displayed rows
     * @param rows new rows to be used
     */
    replaceRows(rows: TableRow<RowDataType, NestedRowDataType>[]) {
        this._rows = rows

        if (this.pagination) {
            this.pagination.rowCount = this._rows.length
        }
    }

    get rows(): TableRow<RowDataType, NestedRowDataType>[] {
        return this._rows
    }

    /**
     * Return the next sort options to be used for parse
     * @param column index of the column that will be sorted
     */
    getNextSortOptions(column: number): CurrentSort {
        const currentSort = this.options.currentSort
        const nextSort: CurrentSort = {
            columnIndex: column,
            ascending: false,
        }

        if (currentSort?.columnIndex === column) {
            if (!currentSort?.ascending) {
                nextSort.ascending = true
            }
        }

        return nextSort
    }

    sort(columnIndex: number) {
        if (!this.options.isSortable) {
            return false
        }
        const columns = this.columns
        const currentSort: CurrentSort = this.options.currentSort
            ? this.options.currentSort
            : {
                  ascending: false,
                  columnIndex: -1,
              }
        const rows = this.rows

        let ascending = false
        if (currentSort?.columnIndex === columnIndex) {
            if (!currentSort?.ascending) {
                ascending = true
            }
        }

        let sortFunction = this.defaultSort
        const providedSortOption = columns[columnIndex].sortAs
        if (typeof providedSortOption === 'function') {
            sortFunction = providedSortOption
        } else {
            if (providedSortOption === 'date') {
                sortFunction = this.dateSort
            } else if (providedSortOption === 'number') {
                sortFunction = this.numberSort
            }
        }

        rows.sort((rowA: TableRow<RowDataType, NestedRowDataType>, rowB: TableRow<RowDataType, NestedRowDataType>) => {
            const objectA = rowA.cellValues[columnIndex]
            const objectB = rowB.cellValues[columnIndex]

            return sortFunction(objectA.value, objectB.value, ascending)
        })

        if (currentSort) {
            currentSort.ascending = ascending
            currentSort.columnIndex = columnIndex
            this.options.currentSort = currentSort
        }

        return true
    }

    private numberSort(rowCellA: CellValue, rowCellB: CellValue, ascending: boolean): -1 | 0 | 1 {
        try {
            const valueA = numeral(rowCellA).value()
            const valueB = numeral(rowCellB).value()
            const difference = valueA - valueB

            if (difference > 0) {
                return ascending ? 1 : -1
            }

            if (difference < 0) {
                return ascending ? -1 : 1
            }
        } catch (err) {}

        return 0
    }

    private dateSort(rowCellA: CellValue, rowCellB: CellValue, ascending: boolean): -1 | 0 | 1 {
        try {
            const valueA = moment(typeof rowCellA !== 'boolean' ? rowCellA : '', 'MM/DD/YYYY h:mm A')
            const valueB = moment(typeof rowCellB !== 'boolean' ? rowCellB : '', 'MM/DD/YYYY h:mm A')
            const difference = valueA.diff(valueB)

            if (difference > 0) {
                return ascending ? 1 : -1
            }

            if (difference < 0) {
                return ascending ? -1 : 1
            }
        } catch (err) {}

        return 0
    }
    private defaultSort(rowCellA: CellValue, rowCellB: CellValue, ascending: boolean): -1 | 0 | 1 {
        try {
            if (ascending) {
                if (rowCellA < rowCellB) {
                    return -1
                }

                if (rowCellB < rowCellA) {
                    return 1
                }
            } else {
                if (rowCellA < rowCellB) {
                    return 1
                }

                if (rowCellB < rowCellA) {
                    return -1
                }
            }
        } catch (err) {}

        return 0
    }
}

export class TableRow<RowDataType, NestedRowDataType> {
    public readonly nestedTable: Table<NestedRowDataType, undefined>
    public areNestedExpanded: boolean
    public readonly isSelectable: boolean
    public isSelected: boolean
    public cellColorValues: string[] = []
    public cellTextColorValues: string[] = []
    private _data: RowData<RowDataType>
    public cellValues: CellValueHolder[] = []
    public displayValues: CellValueHolder[] = []
    private _hoverOptions?: HoverOptions<RowDataType>
    public hoverValues: HoverValueHolder[] = []

    constructor(
        public id: string,
        data: RowData<RowDataType>,
        private columns: TableColumn<RowDataType>[],
        public options: RowOptions,
        nestedData?: NestedTable<NestedRowDataType>
    ) {
        this.nestedTable = nestedData
            ? nestedData.table
            : new Table<NestedRowDataType, undefined>([], [], undefined, {
                  pagination: {
                      show: false,
                  },
                  isSortable: false,
                  selectionOptions: {
                      type: 'NONE',
                  },
                  tableClass: 'nested-table',
              })
        this.areNestedExpanded = nestedData ? nestedData.isExpanded : false
        this.isSelectable = options && options.isSelectable
        this.isSelected = false
        this._data = data
        this.data = data
    }

    hasNestedRows() {
        return this.nestedTable.rows.length > 0
    }

    private updateCellValues() {
        const MAX_LENGTH = 75
        this.cellValues = []
        this.displayValues = []
        for (const column of this.columns) {
            const fn = column.cellValueFn
            const possiblyUndefinedCellValue: CellValue = fn(this.data)
            const cellValue = possiblyUndefinedCellValue === undefined ? '' : possiblyUndefinedCellValue
            let slicedCellValue = cellValue
            if (slicedCellValue) {
                slicedCellValue = `${cellValue}`.length > MAX_LENGTH + 3 ? `${cellValue}`.slice(0, MAX_LENGTH).trim() + '...' : cellValue
            }
            this.cellValues.push({ value: cellValue })
            this.displayValues.push({ value: slicedCellValue })
        }
    }

    private updateHoverValues() {
        this.hoverValues = []
        if (this.hoverOptions && this.hoverOptions.data) {
            for (const fn of this.hoverOptions.valueFn) {
                const value = fn ? fn(this.hoverOptions.data) : undefined
                this.hoverValues.push({ value })
            }
        }
    }

    set data(data: RowData<RowDataType>) {
        this._data = data
        this.updateCellValues()
    }

    get data() {
        return this._data
    }

    set hoverOptions(hoverOptions: HoverOptions<RowDataType> | undefined) {
        this._hoverOptions = hoverOptions
        this.updateHoverValues()
    }
    get hoverOptions(): HoverOptions<RowDataType> | undefined {
        return this._hoverOptions
    }
}

export type ColumnSortOption = 'default' | 'number' | 'date' | ((a: CellValue, b: CellValue, ascending: boolean) => -1 | 0 | 1)
export class TableColumn<RowDataType> {
    public isSortable: boolean
    constructor(
        public readonly label: string,
        public readonly cellValueFn: CellValueFn<RowDataType>,
        public sortAs: ColumnSortOption,
        public readonly fieldName?: string,
        isSortable?: boolean
    ) {
        this.isSortable = isSortable ?? false
    }
}

export class TablePagination {
    rowCount: number
    private _firstRecord = 1
    private _pageSizeText = '25'
    private _pageSizeNumber = 25
    private _skip = 0
    private _limit = 25
    private _serverSide = false
    private _showUnselectButton: boolean
    private _showReloadButton: boolean
    private _showExpandAllButton: boolean
    private _showDownloadButton: boolean

    constructor(
        firstRecord: number,
        pageSize: number,
        rowCount: number,
        serverSide = false,
        showUnselectButton = true,
        showReloadButton = true,
        showDownloadButton = false,
        showExpandAllButton = false
    ) {
        // The order is important
        this.rowCount = rowCount
        this.pageSizeText = '' + pageSize
        this.firstRecord = firstRecord
        this._serverSide = serverSide
        this._showUnselectButton = showUnselectButton
        this._showReloadButton = showReloadButton
        this._showExpandAllButton = showExpandAllButton
        this._showDownloadButton = showDownloadButton
    }

    get showUnselectButton(): boolean {
        return this._showUnselectButton
    }

    get showReloadButton(): boolean {
        return this._showReloadButton
    }

    get showExpandAllButton(): boolean {
        return this._showExpandAllButton
    }

    get showDownloadButton(): boolean {
        return this._showDownloadButton
    }

    get firstRecord(): number {
        // to do fix first record not updating when change page size
        return this._firstRecord
    }

    set firstRecord(value: number) {
        if (this.rowCount === undefined) {
            throw new Error('Pagination needs Max set before you set a record')
        }

        let newValue = value

        if (value < 1) {
            newValue = 1
        }

        if (!this.serverSide && value > this.rowCount) {
            newValue = this.rowCount
        }

        this._firstRecord = newValue
        this.updateSkip()
        this.updateLimit()
    }

    get pageSizeText(): string {
        return this._pageSizeText
    }

    set pageSizeText(value: string) {
        this._pageSizeText = value
        this._pageSizeNumber = Number(value)
        this.updateLimit()
    }

    get pageSizeNumber(): number {
        return this._pageSizeNumber
    }

    get serverSide(): boolean {
        return this._serverSide
    }

    private updateSkip() {
        this._skip = this.firstRecord - 1
    }

    private updateLimit() {
        this._limit = this.firstRecord + this.pageSizeNumber - 1
    }

    get skip() {
        return this._skip
    }

    get limit() {
        return this._limit
    }

    updateRowCount(rowCount: number) {
        this.rowCount = rowCount
    }
}
